import { Signal, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { firstValueFrom, timer } from 'rxjs';

/** Shortest "Verifying…": long enough to read, even when Kratos answers at once. */
const MIN_VERIFY_MS = 1200;

/**
 * The state of one 6-digit code step, shared by every page that asks for a
 * code (sign-up/login, recovery, verification, 2FA, re-authentication).
 * Hand it to `<app-code-entry [step]>`; the page only says what a code (or
 * a resend) leads to.
 */
export interface CodeStep {
  /** The boxes' value. */
  readonly code: FormControl<string>;
  /** A code is being sent or checked; the page's other actions wait. */
  readonly busy: Signal<boolean>;
  /** A complete code is being checked ("Verifying…"). */
  readonly verifying: Signal<boolean>;
  /** The last code was wrong or used. */
  readonly invalid: Signal<boolean>;
  /** Bumped by `reject()`; `app-code-entry` refocuses the boxes on it. */
  readonly refocus: Signal<number>;
  /**
   * Runs one Kratos step (mailing a code, …) while busy. Resolves
   * `undefined` without running it when another step is still running.
   */
  run<T>(step: () => Promise<T>): Promise<T | undefined>;
  /** `run()` for checking a code: shows "Verifying…" for a readable moment. */
  verify<T>(check: () => Promise<T>): Promise<T | undefined>;
  /** A wrong or used code: empty, marked boxes, focused for the next try. */
  reject(): void;
  /** A fresh code went out: empty boxes, no error. */
  reset(): void;
}

export function codeStep(): CodeStep {
  const code = new FormControl('', { nonNullable: true });
  const busy = signal(false);
  const verifying = signal(false);
  const invalid = signal(false);
  const refocus = signal(0);

  const run = async <T>(step: () => Promise<T>): Promise<T | undefined> => {
    if (busy()) {
      return undefined;
    }
    busy.set(true);
    try {
      return await step();
    } finally {
      busy.set(false);
    }
  };

  return {
    code,
    busy: busy.asReadonly(),
    verifying: verifying.asReadonly(),
    invalid: invalid.asReadonly(),
    refocus: refocus.asReadonly(),
    run,
    verify: check =>
      run(async () => {
        invalid.set(false);
        verifying.set(true);
        try {
          const [result] = await Promise.all([
            check(),
            firstValueFrom(timer(MIN_VERIFY_MS)),
          ]);
          return result;
        } finally {
          verifying.set(false);
        }
      }),
    reject: () => {
      code.setValue('');
      invalid.set(true);
      refocus.update(count => count + 1);
    },
    reset: () => {
      code.setValue('');
      invalid.set(false);
    },
  };
}
